import { useCallback, createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Encrypt } from '../utils/encryption';
import { extendToken } from '../config/api';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import {
  clearSubscriptionCatalogCache,
  loadLatestSubscriptionCatalog,
} from '../services/subscriptionCatalog';
import { fetchActiveStoreCount } from '../utils/hubStoreUsage';

const AUTH_STORAGE_KEY = 'zinihub_auth_token';
const EMPTY_STORE_USAGE = Object.freeze({ count: null, status: 'idle' });
const SUBSCRIPTION_RESTRICTION_ERRORS = new Set([
  'SUBSCRIPTION_REQUIRED',
  'SUBSCRIPTION_ACTIVATION_REQUIRED',
  'SUBSCRIPTION_EXPIRED',
  'PLAN_NOT_IN_CATALOG',
  'MODULE_DISABLED',
  'LIMIT_UNAVAILABLE',
  'PLAN_LIMIT_REACHED',
]);

function responseErrorCode(error) {
  const responseData = error?.response?.data;
  if (!responseData) return null;
  if (typeof responseData === 'object' && responseData.error) {
    return responseData.error;
  }
  if (typeof responseData?.body === 'string') {
    try {
      return JSON.parse(responseData.body)?.error || null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Unwrap Lambda proxy `{ body: "<json>" }` or return the object as-is. */
export function unwrapAuthResponse(data) {
  if (!data || typeof data !== 'object') return null;
  if (typeof data.body === 'string') {
    try {
      const parsed = JSON.parse(data.body);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      return data;
    }
  }
  return data;
}

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    try {
      const decoded = jwtDecode(stored);
      if (!decoded?.exp || decoded.exp < Date.now() / 1000) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
      }
      return stored;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  });
  const refreshingRef = useRef(false);
  const hiddenAtRef = useRef(null);
  const catalogLoadKeyRef = useRef('');
  const navigate = useNavigate();
  const [subscriptionCatalogState, setSubscriptionCatalogState] = useState({
    status: 'idle',
    catalog: null,
    error: null,
    publicVersion: null,
    manifest: null,
  });
  const [activeStoreUsageByHub, setActiveStoreUsageByHub] = useState({});
  const activeStoreUsageByHubRef = useRef(activeStoreUsageByHub);
  activeStoreUsageByHubRef.current = activeStoreUsageByHub;
  const activeStoreFetchRef = useRef(new Map());
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const decodeToken = useCallback((tokenValue) => {
    try {
      return jwtDecode(tokenValue);
    } catch {
      return null;
    }
  }, []);

  const setToken = useCallback((initialToken) => {
    const decoded = decodeToken(initialToken);
    if (!decoded?.exp || decoded.exp < Date.now() / 1000) {
      console.warn('❌ Tried to set expired token');
      setTokenState(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      tokenRef.current = null;
    } else {
      setTokenState(initialToken);
      localStorage.setItem(AUTH_STORAGE_KEY, initialToken);
      tokenRef.current = initialToken;
    }
  }, [decodeToken]);

  const logout = useCallback(() => {
    setTokenState(null);
    tokenRef.current = null;
    localStorage.removeItem(AUTH_STORAGE_KEY);
    catalogLoadKeyRef.current = '';
    activeStoreFetchRef.current.clear();
    clearSubscriptionCatalogCache();
    setActiveStoreUsageByHub({});
    setSubscriptionCatalogState({
      status: 'idle',
      catalog: null,
      error: null,
      publicVersion: null,
      manifest: null,
    });
    navigate('/home', { replace: true });
  }, [navigate]);

  const getActiveStoreUsage = useCallback((hubId) => {
    if (!hubId) return EMPTY_STORE_USAGE;
    return activeStoreUsageByHub[hubId] || EMPTY_STORE_USAGE;
  }, [activeStoreUsageByHub]);

  const seedActiveStoreCount = useCallback((hubId, count) => {
    if (!hubId || typeof count !== 'number') return;
    setActiveStoreUsageByHub((current) => {
      const existing = current[hubId];
      if (existing?.status === 'ready' && existing.count === count) {
        return current;
      }
      return {
        ...current,
        [hubId]: { count, status: 'ready' },
      };
    });
  }, []);

  const invalidateActiveStoreCount = useCallback((hubId) => {
    if (!hubId) return;
    activeStoreFetchRef.current.delete(hubId);
    setActiveStoreUsageByHub((current) => {
      if (!current[hubId]) return current;
      const next = { ...current };
      delete next[hubId];
      return next;
    });
  }, []);

  const ensureActiveStoreCount = useCallback(async (hubId, { force = false } = {}) => {
    if (!token || !hubId) return null;

    const existing = activeStoreUsageByHubRef.current[hubId];
    if (!force && existing?.status === 'ready' && typeof existing.count === 'number') {
      return existing.count;
    }

    const inFlight = activeStoreFetchRef.current.get(hubId);
    if (!force && inFlight) return inFlight;

    if (!force && existing?.status === 'loading') {
      return inFlight ?? null;
    }

    setActiveStoreUsageByHub((current) => {
      const previous = current[hubId];
      if (previous?.status === 'loading') return current;
      return {
        ...current,
        [hubId]: { count: previous?.count ?? null, status: 'loading' },
      };
    });

    const request = fetchActiveStoreCount(token, hubId)
      .then((count) => {
        setActiveStoreUsageByHub((current) => {
          const previous = current[hubId];
          if (previous?.status === 'ready' && previous.count === count) {
            return current;
          }
          return {
            ...current,
            [hubId]: { count, status: 'ready' },
          };
        });
        return count;
      })
      .catch(() => {
        setActiveStoreUsageByHub((current) => {
          const previous = current[hubId];
          if (previous?.status === 'error') return current;
          return {
            ...current,
            [hubId]: { count: null, status: 'error' },
          };
        });
        return null;
      })
      .finally(() => {
        activeStoreFetchRef.current.delete(hubId);
      });

    activeStoreFetchRef.current.set(hubId, request);
    return request;
  }, [token]);

  const refreshSubscriptionCatalog = useCallback(async ({ force = true } = {}) => {
    setSubscriptionCatalogState((current) => ({
      ...current,
      status: 'loading',
      error: null,
    }));
    try {
      const loaded = await loadLatestSubscriptionCatalog({ force });
      setSubscriptionCatalogState({
        status: 'ready',
        catalog: loaded.catalog,
        error: null,
        publicVersion: loaded.publicVersion,
        manifest: loaded.manifest,
      });
      return loaded;
    } catch (error) {
      setSubscriptionCatalogState({
        status: 'error',
        catalog: null,
        error,
        publicVersion: null,
        manifest: null,
      });
      throw error;
    }
  }, []);

  const getSubscriptionCatalogLoadKey = useCallback((decoded) => {
    if (!decoded) return '';
    const hubProfiles = [
      ...(decoded.MyHubAdmin || []).map((entry) => entry?.HubProfile),
      ...(decoded.StaffAssignments || []).map((entry) => entry?.HubProfile),
    ].flat().filter(Boolean);
    return JSON.stringify(hubProfiles.map((profile) => ({
      hubId: profile?.PK || '',
      plan: typeof profile?.Plan === 'object'
        ? (profile.Plan?.plan || profile.Plan?.Plan || null)
        : (profile?.Plan || null),
      status: profile?.SubscriptionStatus || '',
      trialUsed: profile?.TrialUsed ?? null,
      startDate: profile?.StartDate || '',
      expiry: typeof profile?.Plan === 'object'
        ? (profile.Plan?.expiry || profile?.ExpiryDate || '')
        : (profile?.ExpiryDate || ''),
      catalogVersion: profile?.CatalogVersion || '',
      catalogChecksum: profile?.CatalogChecksum || '',
    })));
  }, []);

  useEffect(() => {
    if (!token) {
      catalogLoadKeyRef.current = '';
      activeStoreFetchRef.current.clear();
      clearSubscriptionCatalogCache();
      setActiveStoreUsageByHub({});
      setSubscriptionCatalogState({
        status: 'idle',
        catalog: null,
        error: null,
        publicVersion: null,
        manifest: null,
      });
      return;
    }

    const decoded = decodeToken(token);
    const loadKey = getSubscriptionCatalogLoadKey(decoded);
    if (!loadKey || catalogLoadKeyRef.current === loadKey) return;
    catalogLoadKeyRef.current = loadKey;
    refreshSubscriptionCatalog({ force: true }).catch(() => undefined);
  }, [decodeToken, getSubscriptionCatalogLoadKey, refreshSubscriptionCatalog, token]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== AUTH_STORAGE_KEY) return;
      if (!event.newValue) {
        setTokenState(null);
        navigate('/login', { replace: true });
        return;
      }
      const decoded = decodeToken(event.newValue);
      if (!decoded?.exp || decoded.exp < Date.now() / 1000) {
        setTokenState(null);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        navigate('/login', { replace: true });
        return;
      }
      setTokenState(event.newValue);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [decodeToken, navigate]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (SUBSCRIPTION_RESTRICTION_ERRORS.has(responseErrorCode(error))) {
          navigate('/subscription', { replace: true });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [navigate]);


  useEffect(() => {
    let intervalId;

    if (token) {
      const isExpiringSoon = (token, withinSeconds = 606) => {
        const decoded = decodeToken(token);
        if (!decoded?.exp) return true;

        const currentTime = Date.now() / 1000;
        return decoded.exp - currentTime < withinSeconds;
      };

      const refreshToken = async () => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;

        try {
          const response = await axios.post(extendToken, Encrypt(token), {
            headers: {
              'Content-Type': 'application/json',
            },
            skipGlobalErrorToast: true,
          });

          const payload = unwrapAuthResponse(response.data) || response.data;
          const newToken = payload?.token;
          if (newToken) {
            console.log('🔁 Token refreshed');
            setToken(newToken);
          } else {
            console.warn('⚠️ No valid token available');
            logout();
          }
        } catch (error) {
          console.error('🔒 Failed to refresh token:', error);
          logout();
        } finally {
          refreshingRef.current = false;
        }
      };

      const checkAndRefresh = () => {
        if (isExpiringSoon(token)) {
          refreshToken();
        }
      };

      checkAndRefresh(); // ⏱️ Immediate check
      intervalId = setInterval(checkAndRefresh, 1500 * 1000); // ⏳ Every 5 minutes
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          hiddenAtRef.current = Date.now();
          return;
        }
        const hiddenForMs = hiddenAtRef.current
          ? Date.now() - hiddenAtRef.current
          : 0;
        hiddenAtRef.current = null;
        if (hiddenForMs >= 60 * 1000) {
          refreshToken();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        clearInterval(intervalId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    return () => clearInterval(intervalId);
  }, [decodeToken, token, logout, setToken]);

  return (
    <AuthContext.Provider value={{
      token,
      setToken,
      logout,
      decodeToken,
      refreshSubscriptionCatalog,
      subscriptionCatalog: subscriptionCatalogState.catalog,
      subscriptionCatalogError: subscriptionCatalogState.error,
      subscriptionCatalogManifest: subscriptionCatalogState.manifest,
      subscriptionCatalogPublicVersion: subscriptionCatalogState.publicVersion,
      subscriptionCatalogStatus: subscriptionCatalogState.status,
      getActiveStoreUsage,
      seedActiveStoreCount,
      ensureActiveStoreCount,
      invalidateActiveStoreCount,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
